/**
 * Error types for the Breakout SDK
 *
 * All errors have user-friendly messages suitable for display in UI.
 */

// =============================================================================
// Error Codes
// =============================================================================

export type BreakoutErrorCode =
	// Balance errors
	| "INSUFFICIENT_USDC"
	| "INSUFFICIENT_SHARES"

	// Market state errors
	| "MARKET_NOT_FOUND"
	| "MARKET_PENDING"
	| "MARKET_CLOSED"
	| "MARKET_PAUSED"
	| "MARKET_NOT_FINALIZED"
	| "MARKET_ALREADY_RESOLVED"
	| "MARKET_ALREADY_FINALIZED"

	// Validation errors
	| "INVALID_BUCKET"
	| "INVALID_AMOUNT"
	| "SLIPPAGE_EXCEEDED"

	// Claim errors
	| "NOTHING_TO_CLAIM"
	| "ALREADY_CLAIMED"

	// Preparation/execution errors
	| "PREPARATION_EXPIRED"
	| "INVALID_SIGNATURE"
	| "EXECUTION_FAILED"
	| "TRANSACTION_REVERTED"

	// Connection errors
	| "NO_WALLET_CONNECTED"
	| "RPC_ERROR"

	// Permission errors
	| "NOT_CREATOR"
	| "NOT_RESOLVER"
	| "UNAUTHORIZED"

// =============================================================================
// Error Class
// =============================================================================

export class BreakoutError extends Error {
	declare cause?: unknown

	constructor(
		public readonly code: BreakoutErrorCode,
		message: string,
		public readonly details?: Record<string, unknown>,
		options?: { cause?: unknown },
	) {
		super(message)
		this.name = "BreakoutError"
		if (options?.cause) {
			this.cause = options.cause
		}
	}
}

// =============================================================================
// Error Factories
// =============================================================================

export const Errors = {
	// Balance errors
	insufficientUsdc: (needed: number, have: number) =>
		new BreakoutError(
			"INSUFFICIENT_USDC",
			`Insufficient USDC: need $${needed.toFixed(2)}, have $${have.toFixed(2)}`,
			{ needed, have },
		),

	insufficientShares: (bucket: number, needed: number, have: number) =>
		new BreakoutError(
			"INSUFFICIENT_SHARES",
			`Not enough shares in bucket ${bucket}: need ${needed.toFixed(2)}, have ${have.toFixed(2)}`,
			{ bucket, needed, have },
		),

	// Market state errors
	marketNotFound: (address: string) =>
		new BreakoutError("MARKET_NOT_FOUND", `Market not found: ${address}`, { address }),

	marketPending: (opensAt: Date) =>
		new BreakoutError("MARKET_PENDING", `Market opens ${formatRelativeTime(opensAt)}`, { opensAt }),

	marketClosed: () => new BreakoutError("MARKET_CLOSED", "Market is closed for trading"),

	marketPaused: () => new BreakoutError("MARKET_PAUSED", "Market is temporarily paused"),

	marketNotFinalized: () =>
		new BreakoutError("MARKET_NOT_FINALIZED", "Market has not been finalized yet"),

	marketAlreadyResolved: () =>
		new BreakoutError("MARKET_ALREADY_RESOLVED", "Market has already been resolved"),

	marketAlreadyFinalized: () =>
		new BreakoutError("MARKET_ALREADY_FINALIZED", "Market has already been finalized"),

	// Validation errors
	invalidBucket: (bucket: number, max: number) =>
		new BreakoutError("INVALID_BUCKET", `Invalid bucket ${bucket}: must be 0-${max - 1}`, {
			bucket,
			max,
		}),

	invalidAmount: (reason: string) => new BreakoutError("INVALID_AMOUNT", reason),

	slippageExceeded: (expected: number, actual: number) =>
		new BreakoutError(
			"SLIPPAGE_EXCEEDED",
			`Price moved too much: expected $${expected.toFixed(2)}, would pay $${actual.toFixed(2)}`,
			{ expected, actual },
		),

	// Claim errors
	nothingToClaim: () =>
		new BreakoutError("NOTHING_TO_CLAIM", "You have no winning shares to claim"),

	alreadyClaimed: () =>
		new BreakoutError("ALREADY_CLAIMED", "You have already claimed your winnings"),

	// Preparation/execution errors
	preparationExpired: () =>
		new BreakoutError("PREPARATION_EXPIRED", "This transaction has expired. Please try again."),

	invalidSignature: () =>
		new BreakoutError("INVALID_SIGNATURE", "Invalid signature. Please sign again."),

	executionFailed: (reason: string, details?: Record<string, unknown>, cause?: unknown) =>
		new BreakoutError(
			"EXECUTION_FAILED",
			`Transaction failed: ${reason}`,
			{ reason, ...details },
			cause ? { cause } : undefined,
		),

	transactionReverted: (txHash: string) =>
		new BreakoutError("TRANSACTION_REVERTED", "Transaction was reverted on-chain", { txHash }),

	// Connection errors
	noWalletConnected: () =>
		new BreakoutError("NO_WALLET_CONNECTED", "Please connect your wallet to continue"),

	rpcError: (message: string, cause?: unknown) =>
		new BreakoutError("RPC_ERROR", message, { cause }),

	// Permission errors
	notCreator: () =>
		new BreakoutError("NOT_CREATOR", "Only the market creator can perform this action"),

	notResolver: () => new BreakoutError("NOT_RESOLVER", "Only the resolver can perform this action"),

	unauthorized: () =>
		new BreakoutError("UNAUTHORIZED", "You are not authorized to perform this action"),
} as const

// =============================================================================
// Helpers
// =============================================================================

function formatRelativeTime(date: Date): string {
	const now = Date.now()
	const diff = date.getTime() - now

	if (diff < 0) {
		return "in the past"
	}
	if (diff < 60_000) {
		return "in less than a minute"
	}
	if (diff < 3600_000) {
		return `in ${Math.round(diff / 60_000)} minutes`
	}
	if (diff < 86400_000) {
		return `in ${Math.round(diff / 3600_000)} hours`
	}
	return `on ${date.toLocaleDateString()}`
}

// =============================================================================
// Error Type Guards
// =============================================================================

export function isBreakoutError(error: unknown): error is BreakoutError {
	return error instanceof BreakoutError
}

export function hasErrorCode(error: unknown, code: BreakoutErrorCode): boolean {
	return isBreakoutError(error) && error.code === code
}
